import type { PaperMeta } from "../types";

interface PatentMetadataPanelProps {
  meta: PaperMeta;
}

const formatParties = (
  parties?: Array<{ name: string; country_code?: string }> | string[],
): string => {
  if (!parties?.length) return "";
  return parties
    .map((party) => (typeof party === "string" ? party : party.name))
    .filter(Boolean)
    .join(", ");
};

export const PatentMetadataPanel = ({ meta }: PatentMetadataPanelProps) => {
  const rows = [
    ["Publication", meta.publication_number || meta.pub_number],
    ["Application", meta.application_number],
    ["PCT application", meta.pct_number],
    ["Assignee", formatParties(meta.assignees) || meta.assignee],
    ["Inventors", formatParties(meta.inventors)],
    ["Published", meta.publication_date],
    ["Filed", meta.filing_date],
    ["Priority", meta.priority_date],
    ["Granted", meta.grant_date],
  ].filter((row): row is [string, string] => Boolean(row[1]));

  return (
    <section aria-labelledby="patent-metadata-heading">
      <div
        role="status"
        className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        Full text is not available in Paperclip for this publication. This citation refers
        to the patent metadata record shown below.
      </div>

      <h2
        id="patent-metadata-heading"
        className="mb-4 text-lg font-semibold text-zinc-900"
      >
        Patent metadata
      </h2>
      <dl className="divide-y divide-zinc-200 border-y border-zinc-200">
        {rows.map(([label, value]) => (
          <div key={label} className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr] sm:gap-4">
            <dt className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              {label}
            </dt>
            <dd className="text-sm leading-relaxed text-zinc-800">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
